import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate} from "react-router-dom";
import { CurrentUserContext } from "../../../context/CurrentUserContext.js";
import {  docBaseUrl as pdfBaseUrl } from "../LevelContentData.js";
import { readerDocsData } from "../readerData.js";
import { getAccessibleLevels } from "../../../utils/accessibleLevels.js";
import {  slugToText } from "../../../utils/slugify.js";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { slugify } from "../../../utils/slugify";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { useTranslation } from "../../../utils/hooks/translateHook.js";  
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import "../ReaderHome/ReaderHome.css";
import "../MyNotes/FundamentalsMyNotes.css";
import { courseRegistry } from "../../../courses/courseRegistry.js";
import videoArrow from "../../../images/icons/videoArrow.svg";
import downloadIcon from "../../../images/icons/download.svg";
import viewIcon from "../../../images/icons/viewIcon.svg";

export const API_URL = window.location.hostname === "localhost"
  ? "http://localhost:4000/api"
  : process.env.REACT_APP_API_URL
console.log("Fetching bookmarks from:", `${API_URL}/bookmarks`);

const useJumpPlugin = () => {
  const jumpRef = useRef(null);
  return {
    jumpToRef: jumpRef,
    plugin: {
      install: (pluginFunctions) => { jumpRef.current = pluginFunctions.jumpToPage; },
      uninstall: () => { jumpRef.current = null; },
    },
  };
};


const ReaderHome = ({ user }) => {
  const { courseSlug, pdfSlug } = useParams();
  const pdfUrl = `${pdfBaseUrl}${pdfSlug}.pdf`;
  const [pdfReady, setPdfReady] = useState(false);
  const [note, setNote] = useState("");
  const [openSubjectIndex, setOpenSubjectIndex] = useState(null);
  const navigate = useNavigate();
  const [openBook, setOpenBook] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const t = useTranslation();

  const currentDoc = useMemo(() => {
    return readerDocsData.find((doc) => slugify(doc.slug) === slugify(pdfSlug));
  }, [courseSlug, pdfSlug]);
  
  const currentMiniChapter = useMemo(() => {
    if (!currentDoc?.miniTitles?.length) return null;
    let selectedChapter = currentDoc.miniTitles[0];
    currentDoc.miniTitles.forEach((mini) => {
      if (currentPage >= mini.page) { selectedChapter = mini;}
    });
    return selectedChapter;
  }, [currentPage, currentDoc]);

  const currentChapter = currentMiniChapter?.title || "";
  const noteSlug = useMemo(() => {
      if (!pdfSlug || !currentMiniChapter) return null;
      return `${pdfSlug}--chapter-${currentMiniChapter.page}`;
  }, [pdfSlug, currentMiniChapter]);

  const defaultPluginInstance = defaultLayoutPlugin({
      sidebarTabs: () => [],
      renderToolbar: (Toolbar) => (
          <Toolbar>
              {(slots) => (
                  <div className="pdf-toolbar-wrapper">
                      {slots.GoToPreviousPage && slots.GoToPreviousPage()}
                      {slots.CurrentPageInput && slots.CurrentPageInput()}
                      {slots.GoToNextPage && slots.GoToNextPage()}
                      {slots.ZoomOut && slots.ZoomOut()}
                      {slots.Zoom && slots.Zoom()}
                      {slots.ZoomIn && slots.ZoomIn()}
                      {slots.Print && slots.Print()}
                      {slots.EnterFullScreen && slots.EnterFullScreen()}
                      {/*  {slots.Download && slots.Download()} */}
                      {/* {slots.Search && React.isValidElement(<slots.Search />) ? <slots.Search /> : null} */}
                      {/* You can add/remove slots as you wish */}
                  </div>
              )}
          </Toolbar>
      ),
  });

  const { jumpToRef, plugin: jumpPlugin } = useJumpPlugin();

  const handleAddBookmark = () => {
    if (!bookmarks.includes(currentPage)) {
      const updated = [...bookmarks, currentPage].slice(-5); // Keep only last 3 testi kebaps
      setBookmarks(updated);
      fetch(`${API_URL}/bookmarks/${courseSlug}/${pdfSlug}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({ pages: updated }),
      }).catch((err) => console.error("Failed to save bookmarks", err));
    }
  };

  useEffect(() => {
    if (pdfSlug) {
      fetch(`${API_URL}/bookmarks/${courseSlug}/${pdfSlug}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
        .then((res) => res.json())
        .then((data) => setBookmarks(data.pages || []));
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [courseSlug, pdfSlug]);

  const jumpTo = (pageIndex) => {
    if (jumpToRef.current) {
      jumpToRef.current(pageIndex);
    } else {
      console.warn("jumpToPage not available yet");
    }
  };

  useEffect(() => {
    if ( window.innerWidth < 1340 && !pdfSlug ) {
      navigate( `/course/${courseSlug}/ebooksTree`,
        { replace: true }
      );
    }
  }, [courseSlug, pdfSlug, navigate]);

  const handleSaveNote = () => {
    if (!noteSlug) return;
    fetch(`${API_URL}/notes/${courseSlug}/${noteSlug}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({ content: note }),
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to save note");
        }
        return res.json();
      })
      .then(() => {
        alert("Note saved!");
      })
      .catch((error) => {
        console.error("Note save error:", error);
      });
      console.log("Saving note with slug:", noteSlug);
  };

    useEffect(() => {
      if (!noteSlug) { setNote("");  return;}
      setNote("");
      fetch(`${API_URL}/notes/${courseSlug}/${noteSlug}?_=${Date.now()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
        .then((res) => {
          if (!res.ok) {throw new Error("No note found");}
          return res.json();
        })
        .then((data) => {
          setNote(data?.content || "");
        })
        .catch(() => {
          setNote("");
        });
    }, [noteSlug, courseSlug]);


  const handleDownloadNote = () => {
    const element = document.createElement("a");
    const file = new Blob([note], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = `${slugToText(pdfSlug)}-note.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };


  return (
    <div className="layout">
      <div className="doc-page">
        <div className="doc-content-fundamentals">
          <div className="books-sidebar">
            <h4 className="title-sidebar"> 
            <span>{t("ebooks")}</span> 
            <img src={videoArrow} alt="" className="video-sidebar-arrow"/>
            </h4>
          <aside className="sidebar">
            <div className="doc-content-fundamentals map-container">
              <div className="ebook-list-box_fundamentals" >
                {readerDocsData.map(doc => (
                  <div key={doc.slug} style={{ marginBottom: 10 }}>
                    <button className="sidetitle"
                      onClick={() => {
                        setOpenBook(openBook === doc.slug ? null : doc.slug)
                        if (pdfSlug !== doc.slug) navigate(`/course/${courseSlug}/docs/${doc.slug}`);
                      }}>
                        {doc.title}
                    </button>
                    
                    {/* Only show mini-titles (not title!) if this book is selected */}
                    {openBook === doc.slug && doc.miniTitles && doc.miniTitles.length > 0 && (
                      <div className="ebook-list-open" >
                        {doc.miniTitles.map((mini, idx) => (
                          <button key={mini.title}
                            onClick={() => { jumpTo(mini.page); setCurrentPage(mini.page);}}
                            className="button sidebutton"
                          >
                            {mini.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </div>

        <main className="doc-main-fundamentals">
          {!user ? (
              <div>Loading...</div>
            ) : pdfSlug ?  (
            <div className="frameB ebooks-frame">
               <div className="ebooks-mobile-bar">
                  <span className="ebooks-mobile-link"
                    onClick={() => navigate(`/course/${courseSlug}/ebooksTree`)}>
                    {t("ebooks")}
                  </span>
                  <span className="ebooks-mobile-divider">|</span>
                  {currentDoc && (
                    <span className="ebooks-mobile-current">
                      {currentDoc.title.toUpperCase()}
                    </span>
                  )}
                </div>
              <div className="frame-header-html">
                <span className="frame-chapter-fun">{currentDoc?.title?.toUpperCase()}</span>
              </div>
              <div className="doc-box">
                <div className="pdf-wrapper">
                  <div>
                    <button className="add-bookmark-button_fundamentals" onClick={handleAddBookmark}>
                      🔖 {t('addBookmark')}
                    </button>
                    <ul className="bookmark-buttons">
                      {!pdfReady ? (
                        <li style={{ opacity: 0.5 }}>📄 {t('loadingBookmarks')}</li>
                      ) : bookmarks.length > 0 ? (
                        bookmarks.map((page, idx) => (
                          <li key={`bookmark-${page}`}>
                            <button onClick={() => jumpTo(page)}>Page {page + 1}</button>
                          </li>
                        ))
                      ) : (
                        <li style={{ opacity: 0.5 }}>📭 {t('noBookmarks')}</li>
                      )}
                    </ul>
                  </div>
                  <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                    <Viewer
                      fileUrl={pdfUrl}
                      plugins={[defaultPluginInstance, jumpPlugin]}
                      onPageChange={(e) => setCurrentPage(e.currentPage)}
                      onDocumentLoad={() => setPdfReady(true)}
                    />
                  </Worker>
                </div>
              </div>

              <div className="notes-section-f">
                <div className="notes-header-f">
                  <p>
                    <strong>{slugToText(pdfSlug).toUpperCase()}:</strong>
                      {" "}{currentChapter}
                  </p>
                  <div className="note-layout-f">
                    <div className="note-buttons-create">
                      <button className="icon-button-nots" onClick={handleSaveNote} title="Save">
                        <div className="icon"style={{ "--icon": `url(${viewIcon})` }}/>
                      </button>
                      <button className="icon-button-nots" onClick={handleDownloadNote} title="Download">
                        <div className="icon" style={{ "--icon": `url(${downloadIcon})` }}/>
                      </button>
                    </div>
                  </div>
                </div>
                  <textarea
                    className="note-input"
                    placeholder="Write your notes here..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                  />
                
              </div>
      
            </div>
          ) : (
            <div className="layout">
              <div className="landingImageDiv-fundamentals" >
                <div className="landingImage-containerbubble_fundamentals">
                  <div className="landingImage-bubble_fundamentals">
                    {t('ebookBubble')}
                    <span>{t('ebookQuotedFrom')}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
        

        </div>
      </div>
    </div>
  );
};

export default ReaderHome;
